import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const AIAndML = () => {
  const phrases = [
    "Neural Network Optimization",
    "Continuous Inference Grids",
    "Generative Orchestration"
  ];

  const cardData = [
    { type: 'violet-core', content: '🧠' },
    { type: 'matrix-wave', content: '✨' },
    { type: 'tensor-stack', content: '⚙️' },
    { type: 'neural-spark', content: '🔬' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🧠", title: "Custom Deep Learning & Neural Layouts", desc: "Design and train high-performance deep neural network topologies tailored for specialized object detection, semantic text understanding, and signal analytics." },
    { icon: "🤖", title: "Enterprise Generative AI & LLM Systems", desc: "Deploy domain-specific Large Language Model matrices, fine-tuned retrieval setups (RAG), and smart multi-agent swarms to safely automate knowledge work." },
    { icon: "⚙️", title: "End-to-End MLOps Pipeline Automation", desc: "Construct declarative, automated training channels that constantly monitor model drift, run safe validation checks, and update microservices." },
    { icon: "⚡", title: "Sub-Millisecond Accelerated Inference", desc: "Optimize deep learning model weights via quantization, pruning, and compiled acceleration frameworks to drastically lower engine operational latency." },
    { icon: "🗄️", title: "High-Performance Feature Stores", desc: "Establish centralized, low-latency feature calculations to serve identical analytical transformations cleanly to both batch training and live evaluation layers." },
    { icon: "👁️", title: "Advanced Vision & Temporal Systems", desc: "Engineer real-time visual inspection grids, edge camera segmentation pipelines, and complex predictive time-series forecasting networks." },
    { icon: "🛡️", title: "Explainable AI & Alignment Governance", desc: "Incorporate strict bias validation matrices, cryptographic output tracing, and transparent attribution reporting to pass complex compliance reviews." }
  ];

  const advancedServices = [
    { icon: "🔄", title: "Automated Hyperparameter Optimization", desc: "Run elastic cloud search spaces using advanced tuning algorithms to maximize model precision without wasting computational budgets." },
    { icon: "🔬", title: "Dynamic Vector Memory Spaces", desc: "Assemble scalable vector database routing logic to manage billions of structural multi-modal data embeddings under sub-second search bounds." },
    { icon: "⚖️", title: "Synthesized Training Data Generation", desc: "Deploy generative adversarial systems to dynamically manufacture balanced, privacy-compliant training profiles where edge examples are scarce." },
    { icon: "🌍", title: "Distributed Multi-GPU Scaling Frameworks", desc: "Coordinate intensive, massive weight training tasks smoothly across extensive cloud cluster networks using advanced tensor parallelism." },
    { icon: "🔋", title: "TinyML Low-Power Edge Deployment", desc: "Compress heavy predictive weights to compile models directly onto microcontrollers and resource-constrained remote IoT sensors." },
    { icon: "🚨", title: "Real-Time Anomaly Detection Grids", desc: "Weave statistical unsupervised learning nodes directly into manufacturing and transactional data flows to capture system threats instantly." },
    { icon: "📝", title: "Intelligent Semantic Structuring Systems", desc: "Convert vast hoards of unorganized files, scanned imagery, and call documentation into high-value clean database structures automatically." },
    { icon: "🔗", title: "Multi-Modal Sensor Synthesis Engines", desc: "Blend parallel inputs from telemetry channels, audio streams, and radar fields to drive comprehensive machine reasoning systems." },
    { icon: "🧪", title: "Continuous Model Shadow Verifications", desc: "Route operational traffic transparently to experimental models in shadow mode, analyzing real-world safety metrics prior to system changes." }
  ];

  const technologies = {
    "Modeling & Core Frameworks": ["PyTorch / TorchScript", "TensorFlow / Keras", "Hugging Face Transformers", "ONNX Runtime Engines"],
    "Generative AI & LLMOps": ["LangChain / LlamaIndex", "vLLM High-Speed Serving", "OpenAI API Integration", "Pinecone / Milvus / Qdrant"],
    "Infrastructure & MLOps": ["Kubeflow Platform Matrices", "MLflow Tracking Blocks", "Triton Inference Servers", "AWS SageMaker / GCP Vertex"],
    "Data Engineering & Compute": ["Ray / Dask Processing Grids", "Apache Spark Tensor Paths", "dbt AI Transformation Modules", "NVIDIA CUDA Development Toolkit"],
    "Validation & Edge Targets": ["DeepCheck Quality Systems", "TensorRT Engine Compilers", "CoreML / Android NNAPI", "Evidently AI Performance Logs"]
  };

  const processSteps = [
    { num: "01", title: "Data Feasibility Analysis", desc: "Profiling feature densities, auditing class skewness, and mapping programmatic analytical boundaries." },
    { num: "02", title: "Feature Pipeline Design", desc: "Structuring programmatic extraction logic, isolating input keys, and anchoring foundational metrics." },
    { num: "03", title: "Model Exploratory Sprints", desc: "Testing target architectures, running baseline configurations, and analyzing raw loss convergence." },
    { num: "04", title: "Scale Training & Assembly", desc: "Distributing computational workloads across GPU clusters and locking optimized weight checkpoints." },
    { num: "05", title: "Quantization & Safety Hardening", desc: "Compiling code blocks for targeted acceleration, running boundary defense tests, and applying bias criteria." },
    { num: "06", title: "Inference Node Deployment", desc: "Wrapping weights into containerized API microservices with automated traffic routing rules." },
    { num: "07", title: "Closed-Loop Drift Tuning", desc: "Monitoring actual prediction errors, capturing out-of-distribution inputs, and initializing retraining iterations." }
  ];

  const industries = [
    "Algorithmic Risk Rating & FinTech Trading", "Predictive Clinical Diagnosis & Genomics", "Autonomous Fleet Dispatch & Telematics",
    "Automated Quality Check Machine Vision", "Industrial Asset Preventive Maintenance", "Dynamic Supply Chain Flow Planning",
    "High-Density Ad Matching Networks", "Hyper-Personalized Consumer E-Commerce", "Smart Automated Energy Distribution",
    "Secure Fraud Blocking Countermeasures", "Automated Corporate Operations Systems", "Intelligent Content Delivery Networks"
  ];

  const designBenefits = [
    "Automated Discovery of Hidden Features", "Sub-Millisecond System Evaluation Rates", "99%+ Automation in Repetitive Processing Tasks",
    "Continuous Adaptation to System Changes", "Radical Reductions in Manual Analysis Costs", "Highly Optimized Multi-GPU Resource Use",
    "Drastic Reductions in Operational Incident Latency", "Proactive Predictive Business Foresight"
  ];

  const whyChooseUs = [
    "PhD-Level Machine Learning Researchers", "Validated Real-World Production Deployments", "Airtight Data Privacy & Token Security",
    "Deep Model Optimization Competency", "Framework-Agnostic Core Tool Integrations", "Rigorous Mathematical Metric Reporting",
    "End-to-End MLOps Pipeline Ownership", "Transparent Bias Analysis Methodologies", "High-Availability Inference Infrastructure SLAs",
    "Experienced Data Pipeline System Designers", "Iterative Deliverable Prototype Sprints", "100% Mathematically Sound Deployment Guarantees"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          AI & Machine Learning Services | Artificial Intelligence Solutions | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="AI & Machine Learning Services | Artificial Intelligence Solutions | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides AI and Machine Learning services, including generative AI, predictive analytics, NLP, computer vision, intelligent automation, deep learning, AI chatbot development, data science, and custom AI solutions for businesses."
        />

        <meta
          name="keywords"
          content="Artificial Intelligence, AI Services, Machine Learning, AI and ML, Generative AI, AI Development, Machine Learning Services, Predictive Analytics, Natural Language Processing, NLP, Computer Vision, Deep Learning, AI Chatbot Development, Intelligent Automation, Data Science, AI Solutions, Enterprise AI, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/ai-and-ml"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional AI & Machine Learning Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Accelerate innovation with AI and Machine Learning solutions from Affsoft IT Solutions Pvt. Ltd. We specialize in Generative AI, NLP, computer vision, predictive analytics, AI chatbots, deep learning, and intelligent automation."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/ai-and-ml"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AI & Machine Learning Services | Artificial Intelligence Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Transform your business with AI and Machine Learning services from Affsoft IT Solutions Pvt. Ltd. We deliver Generative AI, AI chatbots, NLP, computer vision, predictive analytics, and enterprise AI solutions."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="ai-ml-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0a0f1d' // Deep sapphire/dark space context fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Artificial &nbsp; Intelligence &nbsp; &amp; &nbsp;</span>
              <span>Machine Learning Platforms </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-violet fs-3" style={{ color: '#8b5cf6' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Artificial Intelligence & Machine Learning</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we construct predictive algorithms and intelligent systems that scale. Our machine learning engineering methodology unifies mathematically sound neural architectures with robust MLOps operations to convert raw business data vectors into autonomous strategic engines.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Propose an AI Pilot</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#8b5cf6' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#8b5cf6' }}>Algorithmic Scaling</span>
              <h2 className="fw-bold text-dark display-6">Deploy High-Impact Predictive Intelligence & Neural Architectures</h2>
              <p className="text-muted mt-3">
                Brittle models that drift in production, astronomical computational training costs, and black-box systems that fail governance reviews slow down modernization strategies. At Affsoft IT Solutions Pvt. Ltd., we design mathematically sound, fully tracealled intelligence pipelines.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#8b5cf6' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Advantages of Rigorous ML Engineering</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#8b5cf6' }}>Deep Learning Frameworks</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Automation Capabilities Engineered for Total Accuracy</h2>
              <p className="text-muted mt-3">
                We extend traditional logic modules into highly resilient self-learning infrastructure. From multi-GPU load management setups to time-series anomaly protection, our core frameworks maintain precision under heavy workloads.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#8b5cf6' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#a78bfa' }}>Advanced Tech Matrix</span>
              <h2 className="fw-bold display-6">AI Infrastructure & Deep Learning Toolsets We Leverage</h2>
              <p className="text-light-50">We build, test, and optimize predictive model networks using modern mathematical compute environments.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#a78bfa' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#a78bfa' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Model Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Machine Learning Blueprint</h2>
              <p className="text-muted">A clear, mathematically disciplined development framework designed to guide models safely from validation datasets onto continuous live production tuning.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#8b5cf6' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#8b5cf6' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We pair clean algorithmic implementation styles with modern software operations to ensure reproducible AI integration outcomes.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#8b5cf6' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default AIAndML;